import { PropRow, SortTable } from "~/models/Commons ";
import { TableHeaderType } from "~/models/types/TableHeaderType";
import { SIMPLTableHeader } from "./SimplTableHeader";
import { Pagination } from "../pagination/Pagination";
import { SIMPLTableFilter } from "./SIMPLTableFilter";
import ChipsPropertyScripts from "~/models/scripts/ChipsPropertyScripts";
import "../../../assets/styles/Table.css";
import { LABELS } from "~/resources/Labels";
import { CONTAINER_ITEM_CLASS } from "~/resources/grid";
import { iconStyles, rowStyles } from "~/assets/styles/classes/TableRowClasses";
import { MdDeleteOutline, MdOutlineDownload } from "react-icons/md";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import EditIcon from "@mui/icons-material/Edit";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import { Entries } from "~/models/enums/Entries";
import { ChangeEventHandler } from "react";
import { Tooltip } from "../tooltip/Tooltip";
import DecommissionStatus from "~/models/enums/DecommissionStatus.enum";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { getButtonClasses } from "~/assets/styles/classes/ButtonClasses";
import { getStatusIcon } from "~/Utils/Icons";
import HourglassDisabledIcon from "@mui/icons-material/HourglassDisabled";

interface Prop {
  tableHeaders: TableHeaderType[];
  rows: PropRow[];
  button?: React.ReactNode;
  sortConfig: SortTable;
  active: boolean;
  handleSort: Function;
  chipLabels?: ChipsPropertyScripts[];
  filterActive: boolean;
  showHandler: () => void;
  handlerChips?: () => void;
  handleDecommision?: Function;
  handleRowClick?: Function;
  handleOpenDetail?: Function;
  handleOpenEdit?: Function;
  handleConfigFile?: Function;
  handleDelete?: Function;
  handleDownload?: Function;
  onChange: ChangeEventHandler<HTMLSelectElement>;
  onChangePage: (page: number) => void;
  total: number;
  totalElements: number;
  page: number;
  showPagination: boolean;
  showButtonDecommission?: boolean;
  showButtonDetails?: boolean;
  showButtonDownload?: boolean;
  showButtonDelete?: boolean;
  showConfigFile?: boolean;
  showFilter?: boolean;
  showEdit?: boolean;
  clear: () => void;
  custumizedNoDataAvailable?: string;
}

export const SIMPLTable = (props: Prop) => {
  const {
    custumizedNoDataAvailable = LABELS.NoData,
    total,
    totalElements,
    page,
    showPagination,
    showButtonDecommission,
    showButtonDelete,
    showButtonDetails,
    showButtonDownload,
    showEdit,
    showFilter,
    showConfigFile,
    chipLabels,
    filterActive,
    rows,
    active,
    tableHeaders,
    sortConfig,
    handleSort,
    button,
    showHandler,
    handlerChips,
    handleRowClick,
    handleDecommision,
    handleOpenDetail,
    handleOpenEdit,
    handleDownload,
    handleDelete,
    handleConfigFile,
    onChange,
    onChangePage,
    clear,
  } = props;

  /**
   * Retrieves a value from a nested object based on a specified path.
   *
   * @param {PropRow['row']} row - The object to traverse.
   * @param {string} path - A string representing the path to the desired value, using dot notation.
   * @returns {any} - The value at the specified path, or a label if it's a boolean.
   */
  const getValue = (row: PropRow["row"], path: string) => {
    const paths = path.split(".");
    let root = row;
    paths.forEach((path) => {
      root = root?.[path];
    });

    /* Return a label if the value is a boolean, otherwise return the value*/
    return root;
  };
  /**Allow user to use keyboard Enter */
  const handleKeyAction = (event: React.KeyboardEvent, action: () => void) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      action();
    }
  };

  const onDelete = (row: PropRow["row"]) => handleDelete?.(row);
  const onDownload = (row: PropRow["row"]) => handleDownload?.(row);
  const onDecommission = (row: PropRow["row"]) => handleDecommision?.(row);

  const renderDecommissionChip = () => (
    <div className="flex items-center space-x-1">
      <p className="font-arial text-base font-normal leading-6 text-center">
        {LABELS.Decommision}
      </p>
      <RestoreFromTrashIcon
        fontSize="small"
        className="w-[10.5px] h-[10.5px] text-Primary group-hover:bg-primary-dark hover:text-white transition duration-200"
      />
    </div>
  );

  return (
    <>
      <div className="container-main">
          <div className="container-main flex justify-between items-center mb-4">
          {showFilter ? (
            <SIMPLTableFilter
              clear={clear}
              chipLabels={chipLabels || []}
              filterAtive={filterActive}
              showHandler={showHandler}
              handlerChips={handlerChips}
            />
          ) : (
            <div className="btn-container"></div>
          )}
          <div className="flex-shrink-0">{button}</div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y table-wrapper">
          <SIMPLTableHeader
            headers={tableHeaders}
            showActionsHeader={rows[0]?.tableActions !== undefined}
            sortConfig={sortConfig}
            handleSort={handleSort}
          />
          <tbody>
            {rows.length > 0 ? (
              rows.map((propRow) => (
                <tr
                  id="table-row"
                  key={propRow.row.id}
                  className={`${rowStyles} ${active ? "" : "opacity-45"}`}
                  onClick={() => handleRowClick?.(propRow.row)}
                  onKeyDown={(e) =>
                    handleKeyAction(e, () => handleRowClick?.(propRow.row))
                  }
                  role="row"
                  tabIndex={0}
                >
                  {tableHeaders.map((header, h) => (
                    <td key={h} className={`${CONTAINER_ITEM_CLASS}`}>
                      {header.id === "resourceStatus" &&
                      propRow.row.resourceStatus ? (
                        <div
                          className={getButtonClasses(
                            propRow.row.resourceStatus
                          )}
                        >
                          <div className="flex items-center gap-1">
                            {getStatusIcon(propRow.row.resourceStatus)}
                            <span>{propRow.row[header.id]}</span>
                          </div>
                        </div>
                      ) : (
                        getValue(propRow.row, header.id.toString())
                      )}
                    </td>
                  ))}
                  <td className={`w-full${CONTAINER_ITEM_CLASS}`}>
                    {active && (
                      <div className="w-full h-full text-center">
                        <div className="flex items-center justify-center space-x-12 h-full">
                          {showConfigFile && (
                            <div>
                              <button
                                className="flex items-center justify-center w-[140px] h-[40px] gap-[4px] bg-primary-extra-light border border-Primary text-Primary rounded-full hover:bg-[#032636] hover:text-white transition duration-200 cursor-pointer"
                                onClick={() => handleConfigFile?.(propRow.row)}
                                onKeyDown={(e) =>
                                  handleKeyAction(e, () =>
                                    handleConfigFile?.(propRow.row)
                                  )
                                }
                              >
                                <div className="flex items-center gap-1">
                                  <AddCircleIcon
                                    fontSize="small"
                                    sx={{ width: "13px", color: "#002B6E" }}
                                  />
                                  <span>{LABELS.AddConfig}</span>
                                </div>
                              </button>
                            </div>
                          )}

                          {showButtonDecommission && (
                            <div>
                              {propRow.row.resourceStatus ===
                              DecommissionStatus.ACTIVATED ? (
                                <button
                                  className="flex items-center justify-center w-[140px] h-[40px] gap-[4px] bg-primary-extra-light border border-Primary text-Primary rounded-full hover:bg-[#032636] hover:text-white transition duration-200"
                                  onClick={() => onDecommission?.(propRow.row)}
                                  onKeyDown={(e) =>
                                    handleKeyAction(e, () =>
                                      onDecommission?.(propRow.row)
                                    )
                                  }
                                >
                                  {renderDecommissionChip()}
                                </button>
                              ) : (
                                <div className="w-[130px] h-[32px]" />
                              )}
                            </div>
                          )}

                          {showButtonDetails && (
                            <Tooltip label="Details">
                              <button
                                className={iconStyles}
                                onClick={() => handleOpenDetail?.(propRow.row)}
                                onKeyDown={(e) =>
                                  handleKeyAction(e, () =>
                                    handleOpenDetail?.(propRow.row)
                                  )
                                }
                              >
                                <FileCopyOutlinedIcon
                                  fontSize="small"
                                  sx={{ width: "11px" }}
                                />
                              </button>
                            </Tooltip>
                          )}
                          {showEdit && (
                            <Tooltip label="Edit">
                              <button
                                className={iconStyles}
                                onClick={() => handleOpenEdit?.(propRow.row)}
                                onKeyDown={(e) =>
                                  handleKeyAction(e, () =>
                                    handleOpenEdit?.(propRow.row)
                                  )
                                }
                              >
                                <EditIcon
                                  fontSize="small"
                                  sx={{ width: "15px" }}
                                />
                              </button>
                            </Tooltip>
                          )}
                          {showButtonDownload && (
                            <Tooltip label="Download">
                              <button
                                className={iconStyles}
                                onClick={() => onDownload(propRow.row)}
                                onKeyDown={(e) =>
                                  handleKeyAction(e, () =>
                                    onDownload(propRow.row)
                                  )
                                }
                              >
                                <MdOutlineDownload size={16} />
                              </button>
                            </Tooltip>
                          )}

                          {showButtonDelete && (
                            <Tooltip label="Delete">
                              <button
                                className="hover:text-error-darker text-text-light"
                                id="delete-button"
                                onClick={() => onDelete(propRow.row)}
                                onKeyDown={(e) =>
                                  handleKeyAction(e, () =>
                                    onDelete(propRow.row)
                                  )
                                }
                              >
                                <MdDeleteOutline size={16} />
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr className="h-[400px]">
                <td colSpan={tableHeaders.length + 1} className="text-center">
                  <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                    <HourglassDisabledIcon className="w-12 h-12 mb-2 text-gray-400" />
                    <span className="text-lg font-medium">
                      {custumizedNoDataAvailable}
                    </span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr  className="pagination-wrapper">
              <td colSpan={tableHeaders.length + 1}>
                {rows.length !== 0 && showPagination && (
                  <div>
                    <div className="pagination-footer">
                      <div className="flex items-center p-1 font-sans text-sm font-normal leading-[18px] text-[#37393F]">
                        <p>Item per Page: </p>
                        <select
                          id="number-select"
                          name="number"
                          defaultValue={0}
                          onChange={onChange}
                          className="w-[82.5px] h-[36px] px-4 py-2 gap-2 bg-[#FCFCFC] border border-[#CED0D5] rounded-tl-[2px] rounded-none ml-2"
                        >
                          {Object.values(Entries)
                            .filter((value) => typeof value === "number")
                            .map((value) => (
                              <option key={value} value={value}>
                                {Number(value)}
                              </option>
                            ))}
                        </select>
                        <p className="p-1 font-sans text-sm font-normal leading-[18px] text-left text-[#37393F]">
                          Showing {page + 1}-{total} of {totalElements} Results
                        </p>
                      </div>
                      <Pagination
                        currentPage={page}
                        totalPages={total}
                        onChangePage={onChangePage}
                        hasDataOnLastPage
                      />
                    </div>
                  </div>
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
};
